import {
  AppCard,
  Button,
  Icon,
  PageContainer,
  RouteTabLink,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import {
  mdiAccount,
  mdiClose,
  mdiEye,
  mdiPlus,
  mdiSwapHorizontal,
} from "@mdi/js";
import { TabContext } from "@mui/lab";
import {
  Box,
  Checkbox,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
  useChangeMultipleAssignedToMmRequests,
  useChangeMultipleResponsibleMmRequests,
  useChangeMultipleStatusMmRequests,
  useListMmRequests,
  useMmRequestTabCounts,
} from "../../../../__generated__/orval/endpoints/mm-requests/mm-requests";
import type { EntitiesMmRequestCollectionMain } from "../../../../__generated__/orval/models/entitiesMmRequestCollectionMain.zod";
import type { ListMmRequestsParams } from "../../../../__generated__/orval/models/listMmRequestsParams.zod";
import type { MmRequestTabCountsParams } from "../../../../__generated__/orval/models/mmRequestTabCountsParams.zod";
import { HybridRouteButtonLink } from "../../../../components/molecules/HybridRouteButtonLink";
import { UserInfoBadge } from "../../../../components/molecules/UserInfoBadge";
import { useAuth } from "../../../../hooks/useAuth";
import { i18nextParseToDate } from "../../../../utils/i18nextParseTo";
import { ChangeStatusModal } from "./-internal-components/ChangeStatusModal";
import {
  ChangeAssignedToModal,
  ChangeIssueOwnerModal,
} from "./-internal-components/ChangeUserModal";
import { MmRequestsFilters } from "./-internal-components/MmRequestsFilters";

// region Constants & Schema

const MM_GROUP = "Materials Management";
const MM_REPETRO_GROUP = "Materials Management Repetro Control";
const CHANGE_ISSUE_OWNER_ROLE = "MM Request - Change Issue Owner";
const MM_REQUEST_LEGACY_INTERFACE_STORAGE_KEY = "legacy_interface";
const CHECKBOX_TABS = ["all", "my_requests", "assign_to_me", "open"];
const NON_SELECTABLE_STATUSES = new Set(["Cancelled", "MM Request Finished"]);
const REACT_MM_REQUEST_PATHS: Record<string, string> = {
  "Vbuyer::BillOfMaterialsMgmtRequest":
    "/vbuyer/bill_of_materials_mgmt_requests",
  "Vbuyer::ChangeMaterialRequest": "/vbuyer/change_material_requests",
  "Vbuyer::DuimpRequest": "/vbuyer/duimp_requests",
  "Vbuyer::IssuesOfRfqRequest": "/vbuyer/issues_of_rfq_requests",
  "Vbuyer::OtherMasterdataServicesRequest":
    "/vbuyer/other_masterdata_services_requests",
  "Vbuyer::RnmRequest": "/vbuyer/rnm_requests",
  "Vbuyer::Rsps3Request": "/vbuyer/rsps3_requests",
  "Vbuyer::ValorationRequest": "/vbuyer/valoration_requests",
};

const isMmRequestSelectable = (request: EntitiesMmRequestCollectionMain) =>
  !NON_SELECTABLE_STATUSES.has(request.status?.name ?? "");

const orderSchema = z.object({
  created_at: z.optional(z.enum(["asc", "desc"])).catch(undefined),
  deadline: z.optional(z.enum(["asc", "desc"])).catch(undefined),
  id: z.optional(z.enum(["asc", "desc"])).catch(undefined),
  reason: z.optional(z.enum(["asc", "desc"])).catch(undefined),
  "status.name": z.optional(z.enum(["asc", "desc"])).catch(undefined),
});

const searchSchema = z.object({
  assigned_to: z.optional(z.array(z.coerce.number())).catch(undefined),
  id: z.optional(z.string()).catch(undefined),
  issue_owner: z.optional(z.array(z.coerce.number())).catch(undefined),
  legacy_interface: z.preprocess(
    (value) => (typeof value === "boolean" ? String(value) : value),
    z.enum(["true", "false"]).default("true").catch("true"),
  ),
  material_number: z.optional(z.array(z.string())).catch(undefined),
  order: z
    .optional(orderSchema)
    .default({ deadline: "desc" })
    .catch({ deadline: "desc" }),
  page: z.optional(z.coerce.number()).catch(undefined),
  reason: z.optional(z.array(z.string())).catch(undefined),
  requester: z.optional(z.array(z.coerce.number())).catch(undefined),
  requester_area: z.optional(z.array(z.string())).catch(undefined),
  rowsPerPage: z.optional(z.coerce.number()).catch(undefined),
  status: z.optional(z.array(z.string())).catch(undefined),
  subject: z.optional(z.string()).catch(undefined),
  tab: z
    .enum([
      "all",
      "my_requests",
      "assign_to_me",
      "threat",
      "open",
      "closed",
      "watched_requests",
    ])
    .default("my_requests")
    .catch("my_requests"),
});

type OrderKey = keyof z.infer<typeof orderSchema>;
type TabType = z.infer<typeof searchSchema.shape.tab>;

// endregion

// region Table Row

const MmRequestTableRow: React.FC<{
  isSelected: boolean;
  mmRequest: EntitiesMmRequestCollectionMain;
  onSelect: (id: number) => void;
  showCheckbox: boolean;
}> = ({ isSelected, mmRequest, onSelect, showCheckbox }) => {
  const { t } = useTranslation();
  const reactRequestPath =
    REACT_MM_REQUEST_PATHS[mmRequest.mm_requestable_type ?? ""];
  const reactRequestUrl =
    reactRequestPath && mmRequest.mm_requestable_id !== null
      ? `${reactRequestPath}/${mmRequest.mm_requestable_id}`
      : undefined;

  return (
    <TableRow>
      {!!showCheckbox && (
        <TableCell padding="checkbox">
          <Checkbox
            checked={isSelected}
            disabled={!isMmRequestSelectable(mmRequest)}
            onChange={() => onSelect(mmRequest.id)}
          />
        </TableCell>
      )}
      <TableCell>{mmRequest.id}</TableCell>
      <TableCell>
        {t("intlDateTime", i18nextParseToDate(mmRequest.created_at))}
      </TableCell>
      <TableCell>{mmRequest.status?.name}</TableCell>
      <TableCell>
        {!!mmRequest.deadline &&
          t("intlDate", i18nextParseToDate(mmRequest.deadline))}
      </TableCell>
      <TableCell>{mmRequest.reason}</TableCell>
      <TableCell>{mmRequest.requester_area}</TableCell>
      <TableCell>
        <Tooltip title={mmRequest.subject ?? ""}>
          <Typography
            sx={{
              maxWidth: 200,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            variant="body2"
          >
            {mmRequest.subject}
          </Typography>
        </Tooltip>
      </TableCell>
      <TableCell>
        <Tooltip title={mmRequest.matnumber.join(", ")}>
          <Typography
            sx={{
              maxWidth: 150,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            variant="body2"
          >
            {mmRequest.matnumber.join(", ")}
          </Typography>
        </Tooltip>
      </TableCell>
      <TableCell>
        <UserInfoBadge
          avatarIcon={<Icon icon={mdiAccount} />}
          avatarType="icon"
          info={mmRequest.requester?.full_name}
          photoUser={mmRequest.requester}
          subInfo={mmRequest.requester?.login}
        />
      </TableCell>
      <TableCell>
        <UserInfoBadge
          avatarIcon={<Icon icon={mdiAccount} />}
          avatarType="icon"
          info={mmRequest.assigned_to?.full_name}
          photoUser={mmRequest.assigned_to}
          subInfo={mmRequest.assigned_to?.login}
        />
      </TableCell>
      <TableCell>
        <UserInfoBadge
          avatarIcon={<Icon icon={mdiAccount} />}
          avatarType="icon"
          info={mmRequest.issue_owner?.full_name}
          photoUser={mmRequest.issue_owner}
          subInfo={mmRequest.issue_owner?.login}
        />
      </TableCell>
      <TableCell>
        <HybridRouteButtonLink
          aria-label={t("common.viewRequest")}
          isReact={Boolean(reactRequestUrl)}
          size="small"
          startIcon={<Icon icon={mdiEye} />}
          title={t("common.viewRequest")}
          url={reactRequestUrl ?? `/vbuyer/mm_requests/${mmRequest.id}`}
          variant="text"
        />
      </TableCell>
    </TableRow>
  );
};

// endregion

// region Page

const MmRequestIndex: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const queryClient = useQueryClient();

  const { order, page = 1, rowsPerPage = 25, tab } = search;

  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [assignedModalOpen, setAssignedModalOpen] = useState(false);
  const [responsibleModalOpen, setResponsibleModalOpen] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);

  const canChangeAssigned = Boolean(
    user?.user_groups_names?.includes(MM_GROUP),
  );
  const canChangeResponsible = Boolean(
    user?.roles_names?.includes(CHANGE_ISSUE_OWNER_ROLE) ||
      user?.user_groups_names?.includes(MM_REPETRO_GROUP),
  );
  const showCheckboxes =
    (canChangeAssigned || canChangeResponsible) && CHECKBOX_TABS.includes(tab);

  const listParams: ListMmRequestsParams = {
    assigned_to: search.assigned_to,
    id: search.id,
    issue_owner: search.issue_owner,
    items_per_page: rowsPerPage,
    material_number: search.material_number,
    "order[created_at]": order?.created_at,
    "order[deadline]": order?.deadline,
    "order[id]": order?.id,
    "order[reason]": order?.reason,
    "order[status.name]": order?.["status.name"],
    page,
    reason: search.reason,
    requester: search.requester,
    requester_area: search.requester_area,
    status: search.status,
    subject: search.subject,
    tab: tab === "all" ? undefined : tab,
  };

  const countsParams: MmRequestTabCountsParams = {
    assigned_to: search.assigned_to,
    id: search.id,
    issue_owner: search.issue_owner,
    material_number: search.material_number,
    reason: search.reason,
    requester: search.requester,
    requester_area: search.requester_area,
    status: search.status as MmRequestTabCountsParams["status"],
    subject: search.subject,
  };

  const { data, isError, isLoading } = useListMmRequests(listParams);
  const { data: counts } = useMmRequestTabCounts(countsParams);
  const assignedMutation = useChangeMultipleAssignedToMmRequests();
  const responsibleMutation = useChangeMultipleResponsibleMmRequests();
  const statusMutation = useChangeMultipleStatusMmRequests();

  const requests = data?.results ?? [];
  const totalItems = data?.total_items ?? 0;

  const columnsCount = showCheckboxes ? 15 : 14;

  useEffect(() => {
    if (isError) {
      enqueueSnackbar(t("mmRequests.index.fetchErrorMessage"));
    }
  }, [isError, t]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset selection when tab changes
  useEffect(() => {
    setSelectedItems([]);
  }, [tab]);

  const invalidateMmRequests = useCallback(() => {
    queryClient.invalidateQueries({
      predicate: (query) =>
        query.queryKey.some(
          (key) => typeof key === "string" && key.includes("/mm_requests"),
        ),
    });
  }, [queryClient]);

  const handleSort = useCallback(
    (column: OrderKey) => {
      const direction = order?.[column] === "asc" ? "desc" : "asc";
      navigate({
        search: (prev) => ({
          ...prev,
          order: { [column]: direction },
          page: 1,
        }),
      });
    },
    [navigate, order],
  );

  const handleChangePage = useCallback(
    (_event: unknown, newPage: number) => {
      navigate({ search: (prev) => ({ ...prev, page: newPage + 1 }) });
    },
    [navigate],
  );

  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      navigate({
        search: (prev) => ({
          ...prev,
          page: 1,
          rowsPerPage: parseInt(event.target.value, 10),
        }),
      });
    },
    [navigate],
  );

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: TabType) => {
      navigate({ search: (prev) => ({ ...prev, page: 1, tab: newValue }) });
    },
    [navigate],
  );

  const isSelected = useCallback(
    (id: number) => selectedItems.includes(id),
    [selectedItems],
  );

  const selectableRequestIds = requests
    .filter(isMmRequestSelectable)
    .map((request) => request.id);
  const selectedSelectableCount = selectableRequestIds.filter((requestId) =>
    selectedItems.includes(requestId),
  ).length;
  const isAllSelected =
    selectableRequestIds.length > 0 &&
    selectedSelectableCount === selectableRequestIds.length;

  const handleSelectItem = useCallback((id: number) => {
    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedItems((previousSelectedItems) => {
      if (
        selectableRequestIds.every((requestId) =>
          previousSelectedItems.includes(requestId),
        )
      ) {
        return previousSelectedItems.filter(
          (requestId) => !selectableRequestIds.includes(requestId),
        );
      }

      return Array.from(
        new Set([...previousSelectedItems, ...selectableRequestIds]),
      );
    });
  }, [selectableRequestIds]);

  const handleConfirmAssigned = useCallback(
    (userId: number) => {
      assignedMutation.mutate(
        {
          data: { assigned_to_id: userId, mm_request_ids: selectedItems },
        },
        {
          onError: () =>
            enqueueSnackbar(t("mmRequests.index.updateErrorMessage")),
          onSuccess: () => {
            enqueueSnackbar(
              t("mmRequests.index.changeAssignedToSuccessMessage"),
            );
            setAssignedModalOpen(false);
            setSelectedItems([]);
            invalidateMmRequests();
          },
        },
      );
    },
    [assignedMutation, invalidateMmRequests, selectedItems, t],
  );

  const handleConfirmResponsible = useCallback(
    (userId: number) => {
      responsibleMutation.mutate(
        {
          data: { mm_request_ids: selectedItems, responsible_id: userId },
        },
        {
          onError: () =>
            enqueueSnackbar(t("mmRequests.index.updateErrorMessage")),
          onSuccess: () => {
            enqueueSnackbar(
              t("mmRequests.index.changeResponsibleSuccessMessage"),
            );
            setResponsibleModalOpen(false);
            setSelectedItems([]);
            invalidateMmRequests();
          },
        },
      );
    },
    [responsibleMutation, invalidateMmRequests, selectedItems, t],
  );

  const handleConfirmStatus = useCallback(
    (statusId: number) => {
      statusMutation.mutate(
        { data: { mm_request_ids: selectedItems, status_id: statusId } },
        {
          onError: () =>
            enqueueSnackbar(t("mmRequests.index.updateErrorMessage")),
          onSuccess: () => {
            enqueueSnackbar(t("mmRequests.index.changeStatusSuccessMessage"));
            setStatusModalOpen(false);
            setSelectedItems([]);
            invalidateMmRequests();
          },
        },
      );
    },
    [invalidateMmRequests, selectedItems, statusMutation, t],
  );

  const tabLabel = useCallback(
    (labelKey: string, count?: number) =>
      `${t(labelKey)}${count === undefined ? "" : ` (${count})`}`,
    [t],
  );

  const renderSortableHeader = (column: OrderKey, label: string) => (
    <TableSortLabel
      active={Boolean(order?.[column])}
      direction={order?.[column] ?? "asc"}
      onClick={() => handleSort(column)}
    >
      {label}
    </TableSortLabel>
  );

  return (
    <PageContainer
      maxWidth={false}
      showBackButton={false}
      title={t("mmRequests.index.title")}
    >
      <AppCard>
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            justifyContent: "space-between",
          }}
        >
          <MmRequestsFilters />
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {/* TODO: Remove this button (and legacy_interface handling) when the MM requests React screen is fully migrated. */}
            <Button
              onClick={() => {
                localStorage.setItem(
                  MM_REQUEST_LEGACY_INTERFACE_STORAGE_KEY,
                  "true",
                );
                window.location.assign(
                  "/vbuyer/mm_requests?legacy_interface=true",
                );
              }}
              startIcon={<Icon icon={mdiSwapHorizontal} />}
              variant="outlined"
            >
              {t("mmRequests.index.switchToLegacyButton")}
            </Button>
            <HybridRouteButtonLink
              color="primary"
              startIcon={<Icon icon={mdiPlus} />}
              url="/vbuyer/mm_requests/create_new_requests"
              variant="contained"
            >
              {t("common.newRequest")}
            </HybridRouteButtonLink>
          </Box>
        </Box>

        <TabContext value={tab}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs onChange={handleTabChange} value={tab}>
              <RouteTabLink
                from="/vbuyer/mm_requests/"
                label={tabLabel("common.all", counts?.all)}
                search={(prev) => ({ ...prev, page: 1, tab: "all" })}
                to="/vbuyer/mm_requests"
                value="all"
              />
              <RouteTabLink
                from="/vbuyer/mm_requests/"
                label={tabLabel(
                  "mmRequests.index.myRequestsTab",
                  counts?.my_requests,
                )}
                search={(prev) => ({ ...prev, page: 1, tab: "my_requests" })}
                to="/vbuyer/mm_requests"
                value="my_requests"
              />
              <RouteTabLink
                from="/vbuyer/mm_requests/"
                label={tabLabel(
                  "mmRequests.index.assignToMeTab",
                  counts?.assign_to_me,
                )}
                search={(prev) => ({ ...prev, page: 1, tab: "assign_to_me" })}
                to="/vbuyer/mm_requests"
                value="assign_to_me"
              />
              <RouteTabLink
                from="/vbuyer/mm_requests/"
                label={tabLabel("common.threat", counts?.threat)}
                search={(prev) => ({ ...prev, page: 1, tab: "threat" })}
                to="/vbuyer/mm_requests"
                value="threat"
              />
              <RouteTabLink
                from="/vbuyer/mm_requests/"
                label={tabLabel("mmRequests.index.openTab", counts?.open)}
                search={(prev) => ({ ...prev, page: 1, tab: "open" })}
                to="/vbuyer/mm_requests"
                value="open"
              />
              <RouteTabLink
                from="/vbuyer/mm_requests/"
                label={tabLabel("mmRequests.index.closedTab", counts?.closed)}
                search={(prev) => ({ ...prev, page: 1, tab: "closed" })}
                to="/vbuyer/mm_requests"
                value="closed"
              />
              <RouteTabLink
                from="/vbuyer/mm_requests/"
                label={tabLabel(
                  "mmRequests.index.watchedRequestsTab",
                  counts?.watched_requests,
                )}
                search={(prev) => ({
                  ...prev,
                  page: 1,
                  tab: "watched_requests",
                })}
                to="/vbuyer/mm_requests"
                value="watched_requests"
              />
            </Tabs>
          </Box>

          {selectedItems.length > 0 && showCheckboxes && (
            <Box
              sx={{
                alignItems: "center",
                bgcolor: "tokens.mutedSurface",
                borderRadius: 1,
                display: "flex",
                gap: 2,
                my: 1,
                p: 1,
              }}
            >
              <IconButton onClick={() => setSelectedItems([])}>
                <Icon icon={mdiClose} />
              </IconButton>
              <Typography sx={{ flexGrow: 1 }} variant="subtitle1">
                {t("common.selectedItems", { count: selectedItems.length })}
              </Typography>
              {canChangeAssigned && (
                <Button
                  onClick={() => setAssignedModalOpen(true)}
                  variant="outlined"
                >
                  {t("common.changeAssignedTo")}
                </Button>
              )}
              {canChangeResponsible && (
                <Button
                  onClick={() => setResponsibleModalOpen(true)}
                  variant="outlined"
                >
                  {t("common.changeResponsible")}
                </Button>
              )}
              {canChangeAssigned && (
                <Button
                  onClick={() => setStatusModalOpen(true)}
                  variant="outlined"
                >
                  {t("common.changeStatus")}
                </Button>
              )}
            </Box>
          )}

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {!!showCheckboxes && (
                    <TableCell padding="checkbox">
                      <Tooltip
                        title={
                          isAllSelected
                            ? t("common.uncheckAll")
                            : t("common.checkAll")
                        }
                      >
                        <Checkbox
                          checked={isAllSelected}
                          disabled={selectableRequestIds.length === 0}
                          indeterminate={
                            selectedSelectableCount > 0 && !isAllSelected
                          }
                          onChange={handleSelectAll}
                        />
                      </Tooltip>
                    </TableCell>
                  )}
                  <TableCell>
                    {renderSortableHeader("id", t("common.id"))}
                  </TableCell>
                  <TableCell>
                    {renderSortableHeader("created_at", t("common.createdAt"))}
                  </TableCell>
                  <TableCell>
                    {renderSortableHeader("status.name", t("common.status"))}
                  </TableCell>
                  <TableCell>
                    {renderSortableHeader("deadline", t("common.deadline"))}
                  </TableCell>
                  <TableCell>
                    {renderSortableHeader("reason", t("common.reason"))}
                  </TableCell>
                  <TableCell>{t("common.requesterArea")}</TableCell>
                  <TableCell>{t("common.subject")}</TableCell>
                  <TableCell>{t("common.materialNumber")}</TableCell>
                  <TableCell>{t("common.requester")}</TableCell>
                  <TableCell>{t("common.assignedTo")}</TableCell>
                  <TableCell>{t("common.responsible")}</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {!!isLoading && (
                  <TableRow>
                    <TableCell align="center" colSpan={columnsCount}>
                      <CircularProgress size={28} sx={{ my: 2 }} />
                    </TableCell>
                  </TableRow>
                )}
                {!isLoading && requests.length === 0 && (
                  <TableRow>
                    <TableCell align="center" colSpan={columnsCount}>
                      {t("common.noResultsFound")}
                    </TableCell>
                  </TableRow>
                )}
                {!isLoading &&
                  requests.map((mmRequest) => (
                    <MmRequestTableRow
                      isSelected={isSelected(mmRequest.id)}
                      key={mmRequest.id}
                      mmRequest={mmRequest}
                      onSelect={handleSelectItem}
                      showCheckbox={showCheckboxes}
                    />
                  ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TablePagination
                    count={totalItems}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    page={page - 1}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    showFirstButton
                    showLastButton
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </TabContext>
      </AppCard>

      <ChangeAssignedToModal
        loading={assignedMutation.isPending}
        onClose={() => setAssignedModalOpen(false)}
        onConfirm={handleConfirmAssigned}
        open={assignedModalOpen}
        title={t("common.changeAssignedTo")}
        userLabel={t("common.assignedTo")}
      />
      <ChangeIssueOwnerModal
        loading={responsibleMutation.isPending}
        onClose={() => setResponsibleModalOpen(false)}
        onConfirm={handleConfirmResponsible}
        open={responsibleModalOpen}
        title={t("common.changeResponsible")}
        userLabel={t("common.responsible")}
      />
      <ChangeStatusModal
        loading={statusMutation.isPending}
        mmRequestIds={selectedItems}
        onClose={() => setStatusModalOpen(false)}
        onConfirm={handleConfirmStatus}
        open={statusModalOpen}
        statusLabel={t("common.status")}
        title={t("common.changeStatus")}
      />
    </PageContainer>
  );
};

// endregion

// region Route

export const Route = createFileRoute("/_private/vbuyer/mm_requests/")({
  component: MmRequestIndex,
  validateSearch: searchSchema,
});

// endregion
